import { filterContactInput, contactErrors } from "../../../../utils/contact-validation.ts";
import { useState, type FormEvent } from "react";
import { TextField } from "../../../ui/index.ts";
import { WizardRailLayout } from "../../onboarding-shared/wizard-rail-layout.tsx";
import { AgencyDocumentStep } from "./agency-document-step.tsx";
import { AgencyReviewStep } from "./agency-review-step.tsx";
import { consultantAgencySchema, DOCUMENTS, FIELDS, INITIAL_VALUES, STEPS, validateAgencyFile, type AgencyConsultantValues, type AgencyDocumentId, type AgencyDocuments, type AgencyOnboardingSubmission } from "./consultant-agency-us.config.ts";
import { useOnboardingSubmission } from "../shared/use-onboarding-submission.ts";
import { mapVendorConsultant } from "../shared/submission-mappers.ts";
import styles from "./consultant-agency-us.module.css";

export interface ConsultantAgencyUsFormProps {
  /** Optional override for hosts with a custom submission workflow. */
  onSubmitted?: () => void;
  onSubmit?: (submission: AgencyOnboardingSubmission) => Promise<void>;
}

export function ConsultantAgencyUsForm({ onSubmit, onSubmitted }: ConsultantAgencyUsFormProps) {
  const submission = useOnboardingSubmission("consultant");
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(() => ({ ...INITIAL_VALUES, firstName: submission.verified?.firstName ?? "", lastName: submission.verified?.lastName ?? "", email: submission.verified?.email ?? "" }));
  const [documents, setDocuments] = useState<AgencyDocuments>({});
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AgencyConsultantValues, string>>>({});
  const [documentErrors, setDocumentErrors] = useState<Partial<Record<AgencyDocumentId, string>>>({});
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const updateDocument = (id: AgencyDocumentId, file: File | null) => {
    const error = file ? validateAgencyFile(file) : undefined;
    setDocumentErrors((current) => ({ ...current, [id]: error }));
    setDocuments((current) => {
      const next = { ...current };
      if (file && !error) next[id] = file;
      else delete next[id];
      return next;
    });
  };

  const advance = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || complete) return;
    const invalidContacts = contactErrors(values);
    if (Object.keys(invalidContacts).length) { setFieldErrors(invalidContacts); setStep(0); return; }
    const parsed = consultantAgencySchema.safeParse(values);
    if (!parsed.success) {
      const errors: typeof fieldErrors = {};
      for (const issue of parsed.error.issues) errors[issue.path[0] as keyof AgencyConsultantValues] ??= issue.message;
      setFieldErrors(errors);
      setStep(0);
      return;
    }
    setFieldErrors({});
    setValues(parsed.data);
    if (step === 0) { setStep(1); return; }

    const errors: typeof documentErrors = {};
    for (const document of DOCUMENTS) {
      const file = documents[document.id];
      if (document.required && !file) errors[document.id] = "Please attach this required document.";
      else if (file) {
        const error = validateAgencyFile(file);
        if (error) errors[document.id] = error;
      }
    }
    setDocumentErrors(errors);
    if (Object.keys(errors).length) { setStep(1); return; }
    if (step === 1) { setStep(2); return; }

    setSubmitError(undefined);

    setBusy(true);
    try {
      if (onSubmit) await onSubmit({ consultant: parsed.data, documents: { ...documents } });
      else if (!await submission.submit(mapVendorConsultant(parsed.data), documents)) return;
      setComplete(true);
      onSubmitted?.();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Unable to submit the onboarding form. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <WizardRailLayout
      steps={STEPS}
      currentStepIndex={step}
      sectionTitle={STEPS[step].label}
      onBack={busy || complete ? undefined : () => { setComplete(false); setSubmitError(undefined); setStep((current) => Math.max(0, current - 1)); }}
      primaryLabel={complete ? "Submitted" : step === 2 ? "Submit" : "Continue"}
      primaryDisabled={busy || complete}
      primaryLoading={busy}
      primaryFormId="consultant-agency-us-form"
    >
      <form id="consultant-agency-us-form" onSubmit={(event) => void advance(event)} noValidate>
        <fieldset disabled={busy || complete} className={styles.formFields}>
        {step === 0 && <div className={styles.grid}>
          {FIELDS.map(({ key, label, placeholder, required, type }) => (
            <TextField
              key={key}
              id={`agency-consultant-${key}`}
              label={label}
              required={required}
              readOnly={key === "email" && Boolean(submission.verified)}
              type={type ?? "text"}
              maxLength={150}
              value={values[key]}
              error={fieldErrors[key]}
              placeholder={placeholder}
              onChange={(event) => {
                setValues((current) => ({ ...current, [key]: filterContactInput(key, event.target.value) }));
                setFieldErrors((current) => ({ ...current, [key]: undefined }));
              }}
            />
          ))}
        </div>}
        {step === 1 && <AgencyDocumentStep documents={documents} errors={documentErrors} onChange={updateDocument} />}
        {step === 2 && <>
          <AgencyReviewStep consultant={values} documents={documents} />
          {complete && <p className={styles.notice} role="status">Your onboarding information was submitted successfully.</p>}
          {submitError && <p className={styles.error} role="alert">{submitError}</p>}
        </>}
        </fieldset>
      </form>
    </WizardRailLayout>
  );
}
